import { useContext } from "react";

import { Checkbox } from "@/DesignSystem";
import { EncounterDateContext } from "@/Flowsheets/Contexts/EncounterDateContext";
import { BooleanReviewedCareElement } from "@/Flowsheets/Models/CareElements/BooleanReviewedCareElement";
import { CareElementDateReviewedRow } from "./CareElementDateReviewedRow";

interface CareElementDateReviewedCheckboxProps {
    isEditable: boolean;
    label: string;
    side?: "row" | "column";
    element: BooleanReviewedCareElement;
    onChange: (element: BooleanReviewedCareElement) => void;
}

/**
 * Checkbox for the date reviewed care element.
 * @param props - The properties for the component.
 * @property {BooleanReviewedCareElement} props.element - The care element to be displayed.
 * @property {function} props.onChange - Callback function to handle changes to the care element.
 * @property {boolean} props.isEditable - Indicates if the care element is editable.
 * @property {string} props.label - The label for the care element.
 * @property {string} props.side - The side on which the care element is displayed (row or column).
 * @returns JSX.Element
 */
export default function CareElementDateReviewedCheckbox(props: Readonly<CareElementDateReviewedCheckboxProps>): JSX.Element {
    const { element, onChange, isEditable, side = "row", label } = props;
    const { encounterDateTimeWithPrecision } = useContext(EncounterDateContext);

    return (
        <CareElementDateReviewedRow
            element={element}
            onChange={onChange}
            isEditable={isEditable}
            label={label}
            side={side}
            prefix={(
                <Checkbox
                    id={`checkbox-${element.Name}`}
                    dataCy={`flowsheet-${element.Name}-checkbox`}
                    checked={element.Value}
                    disabled={!isEditable}
                    onChange={value => {
                        const isValid = value === true;

                        const shouldAutoFillDate = isValid && !element.DateReviewedWithPrecision?.value;

                        let dateToUse = null;

                        if (isValid) {
                            dateToUse = shouldAutoFillDate
                                ? encounterDateTimeWithPrecision
                                : element.DateReviewedWithPrecision;
                        }

                        onChange({ ...element, Value: value, DateReviewedWithPrecision: dateToUse });
                    }}
                />
            )}
        />
    );
}
